import type { Point } from 'mapbox-gl';
import mapboxgl, { Evented, LngLat, Map, MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { useHistory } from 'react-router-dom';
import Home from 'components/Home';
import MarkerPoint from 'components/Marker/MarkerPoint';
import PlaceClick from 'components/placeClick/PlaceClick';
import { getSpots } from 'store/slices/spots';
import { changeNewSpot, setSelectedCategories, setSelectedHazards } from 'store/slices/newSpot';
import {
    mouseMapMoveAck,
    moveMapTo,
    setMapInitialized,
    setMapMoving,
    setNeedsUpdate,
    toggleContextualMenu,
    updateCurrentBounds,
    updateCurrentZoom,
} from 'store/slices/map';
import { storeHome } from 'store/slices/home';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import MarkerSummary from 'components/Marker/MarkerSummary';
import kwadHubTheme from 'theme';
import { Backdrop, Box, Button, ThemeProvider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIsSiteRoot } from 'hooks/useIsSiteRoot';
import { MapContComp } from './styled';
import { MapContainerProps, MarkerSpot, MarkerSpotSummary } from './types';
import { SpotShort } from '../../domain';
import { Polygon } from 'geojson'
import { dismissSingletonToast, showSingletonToast } from 'utils/toastSingleton';
import DraftsBanner from 'components/DraftsBanner';
import PendingSpotsBanner from 'components/PendingSpotsBanner';
import MarkerDraft from 'components/Marker/MarkerDraft';
import { fetchDrafts, setActiveDraftId } from 'store/slices/spotDrafts';
import { fetchPendingSpots } from 'store/slices/pendingSpots';
// Mapbox GL Worker setup for CSP compliance
// For detailed information, see the documentation at:
// https://github.com/marianocarpentier/kwadhub-web/docs/MAPBOX_WORKER_SETUP.md

// Additionally, the app runs in development through 'pnpm develop' with react-scripts from CRA and that loads a different set of ESLint rules,
// so the following comments produce errors. So, to prevent those errors I've added in an extra eslint config file that's tailored for this problem.
// .eslintrc.json. Ideally, there would be only one eslint.config.mjs, but react-scripts is compatible with the first one only.

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports,
@typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-var-requires */
// @ts-expect-error Explanation: Required due to Webpack's worker-loader, as ES module imports don't work with this loader
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;
/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports,
@typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-var-requires */

const MapContainer = ({ mapRef }: MapContainerProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const history = useHistory();

    const auth = useAppSelector((state) => state.auth);
    const spotsData = useAppSelector((state) => state.spots);
    const home = useAppSelector((state) => state.home);
    const map = useAppSelector((state) => state.map);
    const { latitude: homeLatitude, longitude: homeLongitude, zoom: homeZoom } = home;
    const {
        isMapMoving,
        center: { lng: mapCenterLng, lat: mapCenterLat },
        zoom: mapZoom,
        mapInitialized,
        isCenterAvailable,
        isByMouseMove,
        isContextualMenuOpen,
        simulateCenterClick,
        needsUpdate,
        searchedLocationMarker
    } = map;

    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const { jwt } = auth;
    const refMap = useRef<Map>(undefined);
    const isMapMovingRef = useRef<boolean>(false);
    const searchedMarkerRef = useRef<mapboxgl.Marker | null>(null);

    const [markersSpots, setMarkersSpots] = useState<MarkerSpot[]>([]);
    const [markersSpotsSummaries, setMarkersSpotsSummaries] = useState<MarkerSpotSummary[]>([]);
    const draftMarkersRef = useRef<mapboxgl.Marker[]>([]);
    const drafts = useAppSelector((state) => state.spotDrafts.drafts);
    const [debugLayers, setDebugLayers] = useState<string[]>([]);
    const preventPopupRef = useRef<boolean>(false);
    const [currentHomeMarker, setCurrentHomeMarker] = useState<mapboxgl.Marker | undefined>(undefined);
    const currentClickEvent = useRef<(e: MapMouseEvent | MapTouchEvent) => void | undefined>(undefined);
    const [postLoginLoadExecuted, setPostLoginLoadExecuted] = useState<boolean>(false);
    const isSiteRoot = useIsSiteRoot();

    const getHandleClickAt = (): ((coords: LngLat) => void) => {
        const handleClick = generateClickEvent();

        return (coords: LngLat) => {
            const syntheticClickEvent = {
                type: 'click',
                lngLat: coords,
                point: refMap.current.project(coords) as Point,
                target: refMap.current,
            } as unknown as MapMouseEvent;

            handleClick(syntheticClickEvent);
        };
    };


    useEffect(() => {
        if (!mapRef.current) return;

        searchedMarkerRef.current?.remove();

        if (searchedLocationMarker) {
            const el = document.createElement('div');
            el.innerHTML = ReactDOMServer.renderToStaticMarkup(
                <ThemeProvider theme={kwadHubTheme}>
                    <MarkerPoint
                        spot={{
                            ...searchedLocationMarker,
                            name: searchedLocationMarker.name,
                            description: '',
                        }}
                        color={theme.palette.primary.contrastText}
                    />
                </ThemeProvider>
            );

            const marker = new mapboxgl.Marker(el, { anchor: 'bottom' })
                .setLngLat([searchedLocationMarker.lng, searchedLocationMarker.lat])
                .addTo(mapRef.current);

            const simulateClickAt = getHandleClickAt();

            el.addEventListener('click', () => {
                preventPopupRef.current = true;

                setTimeout(() => {
                    simulateClickAt(new mapboxgl.LngLat(
                        searchedLocationMarker.lng,
                        searchedLocationMarker.lat
                    ));
                }, 0);
            });

            searchedMarkerRef.current = marker;
        }
    }, [searchedLocationMarker]);

    const {
        spots,
        spotsSummary,
        swLng: swLn,
        swLat: swLa,
        neLat: neLa,
        neLng: neLn,
        gettingSpots,
        zoom: lastRequestedZoom,
    } = spotsData;

    const prevCenterRef = useRef({ mapCenterLat, mapCenterLng });
    const prevBoundingBoxRef = useRef({
        southWestLat: swLa,
        southWestLng: swLn,
        northEastLat: neLa,
        northEastLng: neLn,
    });
    const prevZoomRef = useRef(mapZoom);

    const popupRef = useRef<mapboxgl.Popup | undefined>(undefined);

    useEffect(() => {
        if (!isSiteRoot) {
            handlePopupClose();
        }
    }, [isSiteRoot]);

    useEffect(() => {
        if (simulateCenterClick) {
            handlePopupClose();
            simulateClick();
        } else if (!simulateCenterClick && popupRef.current) {
            handlePopupClose();
        }
    }, [simulateCenterClick, dispatch]);

    const setIsMapMoving = (value: boolean, center?: mapboxgl.LngLat) => {
        if (value === true) {
            dismissSingletonToast();
        }

        isMapMovingRef.current = value;
        dispatch(
            setMapMoving({
                value,
                center,
            })
        );
    };

    const handlePopupClose = () => {
        popupRef.current?.remove();
        popupRef.current = null;
        dispatch(toggleContextualMenu(false));
    };

    const debounce = <T extends (...args: Parameters<T>) => void>(func: T, wait: number) => {
        let timeout: NodeJS.Timeout | null = null;

        return (...args: Parameters<T>) => {
            const later = () => {
                timeout = null;
                func(...args);
            };

            if (timeout) {
                clearTimeout(timeout);
            }

            timeout = setTimeout(later, wait);
        };
    };

    const triggerGetSpotsFromBounds = (map: Map, force = false) => {
        const bounds = map.getBounds();
        const sw = bounds.getSouthWest() as LngLat;
        const newSouthWestLat = sw.lat;
        const newSouthWestLng = sw.lng;
        const ne = bounds.getNorthEast() as LngLat;
        const newNorthEastLat = ne.lat;
        const newNorthEastLng = ne.lng;
        const newZoom = map.getZoom();

        const isBoundingBoxChanged =
            prevBoundingBoxRef.current.southWestLat !== newSouthWestLat ||
            prevBoundingBoxRef.current.southWestLng !== newSouthWestLng ||
            prevBoundingBoxRef.current.northEastLat !== newNorthEastLat ||
            prevBoundingBoxRef.current.northEastLng !== newNorthEastLng;
        const isZoomChanged = prevZoomRef.current !== newZoom;

        if (isBoundingBoxChanged || isZoomChanged || (force && !refMap.current.isMoving())) {
            prevBoundingBoxRef.current = {
                southWestLat: newSouthWestLat,
                southWestLng: newSouthWestLng,
                northEastLat: newNorthEastLat,
                northEastLng: newNorthEastLng,
            };
            prevZoomRef.current = newZoom;

            const newBounds = {
                sw: {
                    lat: newSouthWestLat,
                    lng: newSouthWestLng,
                },
                ne: {
                    lat: newNorthEastLat,
                    lng: newNorthEastLng,
                },
            };

            const boundsChanged =
                swLa !== newSouthWestLat ||
                swLn !== newSouthWestLng ||
                neLa !== newNorthEastLat ||
                neLn !== newNorthEastLng;

            const zoomChanged = lastRequestedZoom !== newZoom;

            const needsUpdate = force || boundsChanged || zoomChanged;

            if (needsUpdate) {
                dispatch(setNeedsUpdate(true));
            }

            dispatch(updateCurrentBounds(newBounds));

            debounceGetSpots(newSouthWestLat, newSouthWestLng, newNorthEastLat, newNorthEastLng, newZoom);
        }
    };

    const needsUpdateRef = useRef(needsUpdate);
    needsUpdateRef.current = needsUpdate;

    const gettingSpotsRef = useRef(gettingSpots);
    gettingSpotsRef.current = gettingSpots;

    const debounceGetSpots = useCallback(
        debounce(
            (
                newSouthWestLat: number,
                newSouthWestLng: number,
                newNorthEastLat: number,
                newNorthEastLng: number,
                newZoom: number
            ) => {
                if (!gettingSpotsRef.current && needsUpdateRef.current) {
                    showSingletonToast('Checking this area...', { autoClose: false, isLoading: true });

                    dispatch(
                        getSpots({
                            swLat: newSouthWestLat,
                            swLng: newSouthWestLng,
                            neLat: newNorthEastLat,
                            neLng: newNorthEastLng,
                            zoom: newZoom,
                        })
                    )
                        .unwrap()
                        .then((res) => {
                            if (res?.spots?.length > 0) {
                                showSingletonToast(`${res.spots.length} spot${res.spots.length > 1 ? 's' : ''} found nearby.`, {
                                    type: 'success',
                                    isLoading: false,
                                    autoClose: 3000,
                                });
                            } else {
                                showSingletonToast(
                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                        <Box sx={{ mr: 2 }}>
                                            No spots found here yet.
                                        </Box>
                                        <Button
                                            sx={{ py: 0 }}
                                            size='small'
                                            variant='contained'
                                            color='secondary'
                                            onClick={() => {
                                                dismissSingletonToast();
                                                history.push('/please-contribute');
                                            }}
                                        >
                                            Discover More
                                        </Button>
                                    </div>,
                                    {
                                        type: 'info',
                                        isLoading: false,
                                        autoClose: false,
                                    }
                                );
                            }
                        })
                        .catch(() => {
                            showSingletonToast('Couldn’t load spots. Please try again.', {
                                type: 'error',
                                isLoading: false,
                                autoClose: 3000,
                            });
                        })
                        .finally(() => {
                            dispatch(setNeedsUpdate(false));
                        });
                }
            },
            1000
        ),
        [dispatch, gettingSpots, needsUpdate]
    );

    // Opens the spot detail
    const showSpotDetail = (spot: SpotShort) => {
        history.push(`/spot-detail/${spot.id}`);
    };

    // Removes all markers
    const removeOrReplaceSpotMarkers = (newMS?: MarkerSpot[]) => {
        markersSpots.forEach((m) => {
            m.marker.remove();
        });
        setMarkersSpots(newMS ?? []);
    };

    const removeOrReplaceSummariesMarkers = (newSM?: MarkerSpotSummary[]) => {
        markersSpotsSummaries.forEach((m) => {
            m.marker.remove();
        });
        setMarkersSpotsSummaries(newSM ?? []);
    };

    // Unloads the spots if jwt is removed.
    useEffect(() => {
        if (!jwt) {
            setPostLoginLoadExecuted(false);
            removeOrReplaceSpotMarkers();
            removeOrReplaceSummariesMarkers();
            if (currentHomeMarker) {
                currentHomeMarker.remove();
            }
        } else if (jwt && !postLoginLoadExecuted) {
            setPostLoginLoadExecuted(true);
            handleLoad();
            void dispatch(fetchDrafts());
            if (auth.tokenData?.data?.superadmin) {
                void dispatch(fetchPendingSpots());
            }
        }
    }, [jwt]);

    // Moves the map to the home if status changes.
    useEffect(() => {
        if (homeLatitude || homeLongitude || homeZoom) {
            dispatch(
                moveMapTo({
                    center: {
                        lng: homeLongitude,
                        lat: homeLatitude,
                    },
                    zoom: homeZoom,
                })
            );
        }
    }, [homeLatitude, homeLongitude, homeZoom]);

    // This stores the center after the map was moved by mouse.
    useEffect(() => {
        if (!isMapMoving && mapCenterLng && mapCenterLat && mapZoom && mapInitialized && !isByMouseMove) {
            const isLatLngChanged =
                prevCenterRef.current.mapCenterLng !== mapCenterLng || prevCenterRef.current.mapCenterLat !== mapCenterLat;

            if (isLatLngChanged) {
                const flySettings = {
                    center: {
                        lng: mapCenterLng,
                        lat: mapCenterLat,
                    },
                    essential: true, // this animation is considered essential with respect to prefers-reduced-motion
                };
                mapRef.current.flyTo(flySettings);

                prevCenterRef.current = {
                    mapCenterLng: mapCenterLng,
                    mapCenterLat: mapCenterLat,
                };
            }
        }
        if (isByMouseMove) {
            dispatch(mouseMapMoveAck());
        }
    }, [isMapMoving, mapCenterLng, mapCenterLat, mapZoom, mapInitialized, isByMouseMove]);

    // When the fetch of new spots is finished, this will add the new spots to the map as 'markers'
    useEffect(() => {
        if (refMap.current && spots && !gettingSpots) {
            const newMarkers = spots.map((s) => {
                const el = document.createElement('div');
                el.innerHTML = ReactDOMServer.renderToStaticMarkup(
                    <ThemeProvider theme={kwadHubTheme}>
                        <MarkerPoint key={`spot-${s.id}`} spot={s}/>
                    </ThemeProvider>
                );
                el.addEventListener('click', () => {
                    preventPopupRef.current = true;
                    showSpotDetail(s);
                });
                return {
                    marker: new mapboxgl.Marker(el, { anchor: 'bottom' }).setLngLat([s.lng, s.lat]).addTo(refMap.current),
                    spot: s,
                };
            });
            removeOrReplaceSpotMarkers(newMarkers);
        }
    }, [spots, gettingSpots, mapInitialized, refMap.current]);

    useEffect(() => {
        if (!refMap.current || !mapInitialized) return;

        draftMarkersRef.current.forEach((m) => m.remove());
        draftMarkersRef.current = [];

        if (!jwt) return;

        draftMarkersRef.current = drafts
            .filter((d) => d.latitude && d.longitude)
            .map((d) => {
                const el = document.createElement('div');
                el.innerHTML = ReactDOMServer.renderToStaticMarkup(
                    <ThemeProvider theme={kwadHubTheme}>
                        <MarkerDraft draft={d} />
                    </ThemeProvider>
                );
                el.addEventListener('click', () => {
                    preventPopupRef.current = true;
                    void dispatch(changeNewSpot({
                        latitude: d.latitude,
                        longitude: d.longitude,
                        zoom: d.zoom,
                        name: d.name ?? '',
                        description: d.description ?? '',
                        youTubeLink: d.youTubeLink ?? '',
                        privacy: d.privacy,
                        anonymity: d.anonymity,
                    }));
                    dispatch(setSelectedCategories(d.selectedCategories ?? []));
                    dispatch(setSelectedHazards(d.selectedHazards ?? []));
                    dispatch(setActiveDraftId(d.id));
                    const stepRoutes: Record<number, string> = { 1: '/add-spot', 2: '/add-spot/step-2', 3: '/add-spot/step-3', 4: '/add-spot/step-4' };
                    history.push(stepRoutes[d.step] ?? '/add-spot');
                });
                return new mapboxgl.Marker(el, { anchor: 'bottom' })
                    .setLngLat([d.longitude, d.latitude])
                    .addTo(refMap.current);
            });
    }, [drafts, jwt, mapInitialized, refMap.current]);

    const getZoomLevels = (): number[] => {
        // TODO: This is a rip off of APIs method. Make it a parameter that comes from the API at start.
        const minZoom = 1;
        const maxZoom = 16;
        const zoomStep = 0.5;
        const zoomLevels: number[] = [];
        for (let i = minZoom; i <= maxZoom; i = i + zoomStep) {
            zoomLevels.push(i);
        }
        return zoomLevels;
    };

    useEffect(() => {
        const addSummaryMarkers = () => {
            const maxNumber = spotsSummary.reduce((max, current) => (current.sum > max.sum ? current : max), {
                sum: -Infinity,
            })?.sum;
            const minNumber = spotsSummary.reduce((min, current) => (current.sum < min.sum ? current : min), {
                sum: Infinity,
            })?.sum;
            const newSummaryMarkers = spotsSummary.map((s) => {
                const el = document.createElement('div');
                el.innerHTML = ReactDOMServer.renderToStaticMarkup(
                    <ThemeProvider theme={kwadHubTheme}>
                        <MarkerSummary key={`spot-${s.id}`} spotSummary={s} maxNumber={maxNumber}
                            minNumber={minNumber}
                        />
                    </ThemeProvider>
                );
                return {
                    marker: new mapboxgl.Marker(el, { anchor: 'bottom' })
                        .setLngLat([s.longitudeSpot, s.latitudeSpot])
                        .addTo(refMap.current),
                    spotSummary: s,
                } as MarkerSpotSummary;
            });
            if (process.env.REACT_APP_DEBUG_MAP === 'true') {
                const coordinates: Polygon['coordinates'] = [];
                let zoom = mapRef.current.getZoom();
                newSummaryMarkers
                    .map((mss) => mss.spotSummary)
                    .filter((s) => {
                        const closestZoomLevel = getZoomLevels().reduce((prev, curr) =>
                            Math.abs(curr - zoom) < Math.abs(prev - zoom) ? curr : prev
                        );
                        console.log('actual zoom', zoom);
                        console.log('closestZoomLevel', closestZoomLevel);
                        console.log('s.zoom', s.zoom);
                        console.log('s.zoom === closestZoomLevel', s.zoom === closestZoomLevel);
                        return s.zoom === closestZoomLevel;
                    })
                    .forEach((s) => {
                        const polygon = [
                            [s.longitudeMin, s.latitudeMin], // southwest corner [lng, lat]
                            [s.longitudeMin, s.latitudeMax], // northwest corner [lng, lat]
                            [s.longitudeMax, s.latitudeMax], // northeast corner [lng, lat]
                            [s.longitudeMax, s.latitudeMin], // southeast corner [lng, lat]
                            [s.longitudeMin, s.latitudeMin],
                        ];
                        coordinates.push(polygon);
                        zoom = s.zoom;
                    });
                console.log('**** DEBUGGING MAP ****', coordinates);
                const id = `my-polygon-${new Date().getTime()}`;
                debugLayers.forEach((id) => refMap.current.removeLayer(id));
                refMap.current.addLayer({
                    id,
                    type: 'line',
                    source: {
                        type: 'geojson',
                        data: {
                            type: 'Feature',
                            properties: {},
                            geometry: {
                                type: 'Polygon',
                                coordinates,
                            },
                        },
                    },
                    layout: {},
                    paint: {
                        'line-color': '#008800',
                        'line-width': 2,
                    },
                });
                setDebugLayers([id]);
            }
            removeOrReplaceSummariesMarkers(newSummaryMarkers);
        };

        if (refMap.current && spotsSummary && !gettingSpots && mapInitialized) {
            if (refMap.current.isStyleLoaded()) {
                console.log('Adding summary markers...');
                addSummaryMarkers();
            } else {
                console.log('Style not loaded yet, postponing the addition of summary markers...');
                refMap.current.once('style.load', addSummaryMarkers);
            }
        }
    }, [spotsSummary, gettingSpots, mapInitialized, refMap.current]);

    useEffect(() => {
        if (refMap && refMap.current && homeLatitude && homeLongitude) {
            if (currentHomeMarker) {
                currentHomeMarker.remove();
            }
            const el = document.createElement('div');
            el.innerHTML = ReactDOMServer.renderToStaticMarkup(
                <ThemeProvider theme={kwadHubTheme}>
                    <Home/>
                </ThemeProvider>
            );
            el.addEventListener('click', () => {
                triggerCenter(); // MouseEvent won't do here, it's better empty...
            });
            setCurrentHomeMarker(
                new mapboxgl.Marker(el, { anchor: 'bottom' }).setLngLat([homeLongitude, homeLatitude]).addTo(refMap.current)
            );
        }
    }, [homeLatitude, homeLongitude, refMap.current]);

    useEffect(() => {
        if (refMap && refMap.current) {
            dispatch(updateCurrentZoom(refMap.current.getZoom()));
            dispatch(setMapInitialized(true));
        }
    }, [refMap?.current]);

    // I reuse this to fly center of 'Home' (paramters are undefined)
    const triggerCenter = (e?: mapboxgl.MapMouseEvent) => {
        const center: { lng: number; lat: number } = e
            ? e.lngLat
            : {
                lng: homeLongitude,
                lat: homeLatitude,
            };
        const zoom = e ? mapRef.current.getZoom() : homeZoom;
        dispatch(moveMapTo({ center, zoom }));
        handlePopupClose();
    };

    const simulateClick = () => {
        if (refMap.current) {
            const center = refMap.current.getCenter() as mapboxgl.LngLat;
            const point = refMap.current.project(center) as mapboxgl.PointLike;

            const syntheticClickEvent = {
                type: 'click',
                lngLat: center,
                point,
                target: refMap.current,
            } as unknown as MapMouseEvent;

            const handleSingleTap = generateClickEvent();
            handleSingleTap(syntheticClickEvent);
        }
    };

    const generateClickEvent = () => {
        const handleSingleTap = (e: MapMouseEvent | MapTouchEvent) => {
            if (preventPopupRef.current) {
                preventPopupRef.current = false;
                return;
            }

            const popup = new mapboxgl.Popup({ anchor: 'center' }).setLngLat(e.lngLat);
            const placeholder = document.createElement('div');
            ReactDOM.render(
                <ThemeProvider theme={theme}>
                    <PlaceClick
                        addNew={() => {
                            history.push('/add-spot');
                            const {
                                lat: latitude,
                                lng: longitude,
                            } = e && 'lngLat' in e ? e.lngLat : { lat: 0, lng: 0 };
                            void dispatch(
                                changeNewSpot({
                                    latitude,
                                    longitude,
                                })
                            );
                            handlePopupClose();
                        }}
                        center={() => {
                            triggerCenter(e as MapMouseEvent);
                        }}
                        setHome={
                            jwt
                                ? () => {
                                    showSingletonToast('Storing new home...', { isLoading: true, autoClose: false });
                                    void dispatch(
                                        storeHome({
                                            latitude: e.lngLat?.lat,
                                            longitude: e.lngLat?.lng,
                                            zoom: mapRef.current.getZoom(),
                                        })
                                    )
                                        .unwrap()
                                        .then(() => {
                                            showSingletonToast('The new home location has been set', {
                                                type: 'success',
                                                isLoading: false,
                                                autoClose: 3000,
                                            });
                                        })
                                        .catch(() => {
                                            showSingletonToast(
                                                'Error occurred while storing the new home. ' +
                                                'If the problem persists, please contact us.',
                                                {
                                                    type: 'error',
                                                    isLoading: false,
                                                    autoClose: 3000,
                                                }
                                            );
                                        });
                                    handlePopupClose();
                                }
                                : null
                        }
                        close={handlePopupClose}
                    />
                </ThemeProvider>
                ,
                placeholder
            );
            popup.setDOMContent(placeholder).addTo(mapRef.current);
            popupRef.current = popup;
            dispatch(toggleContextualMenu(true));
        };

        return (e: MapMouseEvent | MapTouchEvent) => {
            if (e.type === 'click') {
                handleSingleTap(e);
            }
        };
    };

    // The click listener is unfortunately not defined inside the main app Provider, so it can't access redux.
    // So I need to add and remove the click listener if the user logs in or out.
    useEffect(() => {
        if (mapRef.current) {
            if (currentClickEvent.current) {
                mapRef.current.off('click', currentClickEvent.current);
            }
            currentClickEvent.current = generateClickEvent();
            mapRef.current.on('click', currentClickEvent.current);

        }
        return () => {
            if (mapRef.current && currentClickEvent.current) {
                mapRef.current.off('click', currentClickEvent.current);
            }
        };
    }, [jwt, mapRef.current]);

    const isMap = (target: Evented): target is mapboxgl.Map => {
        return target instanceof mapboxgl.Map;
    }

    const handleMoveEnd = (e: mapboxgl.MapboxEvent<MouseEvent | TouchEvent>) => {
        if (e?.target && isMap(e.target)) {
            const c = e.target.getCenter() as LngLat;
            setIsMapMoving(false, c);
            triggerGetSpotsFromBounds(e.target);
        } else {
            console.warn('e.target is not a map instance.');
        }
    };

    const handleMoveStart = () => {
        setIsMapMoving(true);
    };

    const handleLoad = () => {
        if (refMap.current)
            triggerGetSpotsFromBounds(refMap.current, true);
    };

    useEffect(() => {
        if (isCenterAvailable && mapCenterLng && mapCenterLat && !refMap.current) {
            const mapData = {
                container: mapContainerRef.current,
                style: 'mapbox://styles/mapbox/dark-v10',
                attributionControl: false,
                center: [mapCenterLng, mapCenterLat] as [number, number],
                zoom: 12,
            };
            const map = new mapboxgl.Map(mapData);
            map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
            map.on('moveend', handleMoveEnd);
            map.on('movestart', handleMoveStart);
            map.on('load', handleLoad);
            refMap.current = map;
            if (typeof mapZoom === 'undefined') {
                dispatch(updateCurrentZoom(map.getZoom()));
            }
            if (mapRef) {
                mapRef.current = map;
            }
        }
    }, [isCenterAvailable, mapCenterLng, mapCenterLat]);

    useEffect(() => {
        return () => {
            if (refMap.current) {
                refMap.current.off('moveend', handleMoveEnd);
                refMap.current.off('movestart', handleMoveStart);
                refMap.current.off('load', handleLoad);
            }
        };
    }, []);

    return (
        <>
            <MapContComp ref={mapContainerRef}/>
            <DraftsBanner />
            <PendingSpotsBanner />
            <Backdrop
                open={isContextualMenuOpen}
                onClick={handlePopupClose}
                sx={{
                    zIndex: (theme) => theme.zIndex.appBar + 2,
                    color: '#fff',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
            />
        </>
    );
};

export default MapContainer;
